/**
 * Tabs Section logic extracted from section template.
 * Ensures idempotent initialization even when multiple Tab sections exist or re-render via Shopify section rendering.
 */
(function () {
	const GLOBAL_FLAG = "__TABS_SECTION_SCRIPT_LOADED__";
	/** @type {any} */ (window)[GLOBAL_FLAG] =
		/** @type {any} */ (window)[GLOBAL_FLAG] || false;
	if (/** @type {any} */ (window)[GLOBAL_FLAG]) return; // Prevent evaluating the module body twice
	/** @type {any} */ (window)[GLOBAL_FLAG] = true;

	class TabSwitcher {
		/**
		 * @param {HTMLElement} container element with class .tabs
		 */
		constructor(container) {
			this.container = container;
			this.tabs = Array.from(this.container.querySelectorAll('[role="tab"]'));
			this.panels = Array.from(
				this.container.querySelectorAll('[role="tabpanel"]')
			);
			this.anchorButtons = Array.from(
				this.container.querySelectorAll(
					'.tabs__nav-button[data-anchor-target]:not([role="tab"])'
				)
			);
			this.collapsible =
				this.container.getAttribute("data-collapsible") === "true";
			this.isCollapsed = false; // Tracks if current active panel is collapsed when collapsible enabled
			this.currentTab = 0;
			this.initialized = false;
			this.init();
		}

		init() {
			if (this.initialized) return;
			// Always bind anchor buttons even if there are no panels/tabs
			this.bindAnchorEvents();
			if (this.tabs.length && this.panels.length) {
				this.bindTabEvents();
				this.setInitialTab();
			}
			this.initialized = true;
		}

		bindTabEvents() {
			this.tabs.forEach((tab, index) => {
				tab.addEventListener("click", (event) => {
					event.preventDefault();
					this.switchToTab(index);
				});
				tab.addEventListener("keydown", (event) => {
					this.handleKeydown(/** @type {KeyboardEvent} */ (event), index);
				});
			});
		}

		bindAnchorEvents() {
			this.anchorButtons.forEach((btn) => {
				if (!(btn instanceof HTMLElement)) return;
				if (btn.dataset.anchorBound === "true") return; // prevent duplicate binding
				btn.dataset.anchorBound = "true";
				btn.addEventListener("click", (event) => {
					event.preventDefault();
					let targetId = btn.dataset.anchorTarget || "";
					if (!targetId) return;
					if (targetId.startsWith("#")) targetId = targetId.slice(1);
					const offset = parseInt(btn.dataset.anchorOffset || "0", 10) || 0;
					const targetEl = document.getElementById(targetId);
					if (!targetEl) return;
					const rect = targetEl.getBoundingClientRect();
					const absoluteTop = rect.top + window.pageYOffset + offset;
					window.scrollTo({ top: absoluteTop, behavior: "smooth" });
				});
			});
		}

		setInitialTab() {
			if (this.collapsible) {
				// All panels remain collapsed initially; ensure proper collapsed inline styles
				this.panels.forEach((panel) => {
					if (!(panel instanceof HTMLElement)) return;
					panel.classList.add("is-collapsed");
					panel.style.height = "0px";
				});
				this.tabs.forEach((tab) => {
					if (!(tab instanceof HTMLElement)) return;
					tab.setAttribute("aria-selected", "false");
				});
				this.currentTab = 0; // logical index; nothing open
				this.isCollapsed = true;
				return;
			}
			// Non-collapsible retains existing behavior
			const activeTabIndex = this.tabs.findIndex(
				(tab) => tab.getAttribute("aria-selected") === "true"
			);
			if (activeTabIndex !== -1) this.switchToTab(activeTabIndex);
			else this.switchToTab(0);
		}

		/**
		 * @param {number} index
		 */
		switchToTab(index) {
			if (index < 0 || index >= this.tabs.length) return;
			// If collapsing is enabled and the user clicks the current active tab, collapse instead of switching
			if (this.collapsible && index === this.currentTab) {
				this.toggleCollapse();
				return;
			}
			this.showTab(index);
		}

		/**
		 * Explicitly show a tab (internal use). Always expands, resets collapsed state.
		 * @param {number} index
		 */
		showTab(index) {
			const prevTab = this.tabs[this.currentTab];
			const prevPanel = this.panels[this.currentTab];
			if (prevTab instanceof HTMLElement) {
				prevTab.setAttribute("aria-selected", "false");
				prevTab.classList.remove("tabs__nav-button--active");
			}
			if (prevPanel instanceof HTMLElement) {
				if (this.collapsible) {
					this.animateCollapse(prevPanel); // will not add tabs__panel--hidden now
				} else prevPanel.classList.add("tabs__panel--hidden");
			}
			this.currentTab = index;
			const newTab = this.tabs[this.currentTab];
			const newPanel = this.panels[this.currentTab];
			if (newTab instanceof HTMLElement) {
				newTab.setAttribute("aria-selected", "true");
				newTab.classList.add("tabs__nav-button--active");
			}
			if (newPanel instanceof HTMLElement) {
				if (this.collapsible) {
					this.preparePanelForAnimation(newPanel);
					this.animateExpand(newPanel);
				} else {
					newPanel.classList.remove("tabs__panel--hidden");
				}
			}
			this.isCollapsed = false;
		}

		/**
		 * Collapse or expand the currently active tab panel when collapsible enabled.
		 */
		toggleCollapse() {
			if (!this.collapsible) return;
			const activePanel = this.panels[this.currentTab];
			const activeTab = this.tabs[this.currentTab];
			if (
				!(activePanel instanceof HTMLElement) ||
				!(activeTab instanceof HTMLElement)
			)
				return;
			if (this.isCollapsed) {
				// expand
				activeTab.setAttribute("aria-selected", "true");
				activeTab.classList.add("tabs__nav-button--active");
				this.preparePanelForAnimation(activePanel);
				this.animateExpand(activePanel, () => {
					this.isCollapsed = false;
				});
			} else {
				// collapse
				activeTab.setAttribute("aria-selected", "false");
				activeTab.classList.remove("tabs__nav-button--active");
				this.animateCollapse(activePanel, () => {
					this.isCollapsed = true;
				});
			}
		}

		/**
		 * Ensure panel has starting styles for animation
		 * @param {HTMLElement} panel
		 */
		preparePanelForAnimation(panel) {
			panel.classList.remove("tabs__panel--hidden");
			panel.classList.remove("is-collapsed");
			panel.style.paddingTop = "";
			panel.style.paddingBottom = "";
		}

		/**
		 * Animate expanding a panel
		 * @param {HTMLElement} panel
		 * @param {() => void} [done]
		 */
		animateExpand(panel, done) {
			const startHeight = panel.getBoundingClientRect().height; // current (likely 0)
			panel.style.height = "auto";
			const targetHeight = panel.getBoundingClientRect().height;
			panel.style.height = startHeight + "px";
			panel.classList.add("is-expanding");
			requestAnimationFrame(() => {
				panel.style.height = targetHeight + "px";
			});
			/** @param {TransitionEvent} e */
			const onEnd = (e) => {
				if (e.propertyName !== "height") return;
				panel.classList.remove("is-expanding");
				panel.style.height = "auto";
				panel.removeEventListener("transitionend", onEnd);
				if (done) done();
			};
			panel.addEventListener("transitionend", onEnd);
		}

		/**
		 * Animate collapsing a panel
		 * @param {HTMLElement} panel
		 * @param {() => void} [done]
		 */
		animateCollapse(panel, done) {
			// Guard against double collapse
			if (panel.classList.contains("is-collapsed")) {
				if (done) done();
				return;
			}
			const startHeight = panel.getBoundingClientRect().height;
			panel.style.height = startHeight + "px";
			panel.classList.add("is-collapsing");
			panel.classList.remove("is-collapsed");
			requestAnimationFrame(() => {
				panel.style.height = "0px";
				panel.classList.add("is-collapsed");
			});
			/** @param {TransitionEvent} e */
			const onEnd = (e) => {
				if (e.propertyName !== "height") return;
				panel.classList.remove("is-collapsing");
				panel.style.height = "0px";
				panel.removeEventListener("transitionend", onEnd);
				// No longer toggling tabs__panel--hidden for collapsible mode; panel remains accessible for expansion
				if (done) done();
			};
			panel.addEventListener("transitionend", onEnd);
		}

		/**
		 * @param {KeyboardEvent} event
		 * @param {number} index
		 */
		handleKeydown(event, index) {
			let newIndex = index;
			switch (event.key) {
				case "ArrowLeft":
					event.preventDefault();
					newIndex = index > 0 ? index - 1 : this.tabs.length - 1;
					break;
				case "ArrowRight":
					event.preventDefault();
					newIndex = index < this.tabs.length - 1 ? index + 1 : 0;
					break;
				case "Home":
					event.preventDefault();
					newIndex = 0;
					break;
				case "End":
					event.preventDefault();
					newIndex = this.tabs.length - 1;
					break;
				default:
					return;
			}
			this.switchToTab(newIndex);
			const focusTarget = this.tabs[newIndex];
			if (focusTarget instanceof HTMLElement) focusTarget.focus();
		}
	}

	/**
	 * Initialize all tab sections within a root node
	 * @param {ParentNode} root
	 */
	function initAllTabSections(root = document) {
		const containers = root.querySelectorAll(".tabs");
		containers.forEach((el) => {
			const element =
				/** @type {HTMLElement & { __tabsInstance?: TabSwitcher }} */ (el);
			if (element.__tabsInstance) return; // already initialized
			element.__tabsInstance = new TabSwitcher(element);
		});
	}

	// DOM ready initialization
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => initAllTabSections());
	} else {
		initAllTabSections();
	}

	// Support Shopify Section Rendering events if present
	document.addEventListener("shopify:section:load", (e) => {
		const container = e.target;
		if (container instanceof HTMLElement) initAllTabSections(container);
	});
	document.addEventListener("shopify:section:select", (e) => {
		const container = e.target;
		if (container instanceof HTMLElement) initAllTabSections(container);
	});
})();
